import {observable, computed, action, reaction, ObservableMap, makeObservable} from 'mobx';
import {SearchesStore} from './SearchesStore';
import {Filter} from './Filter';
import {getLocalizedText, texts} from '../../../constants/TextConst';
import {getDefaultOperator} from '../consts/operators';
import Cookies from 'universal-cookie';
import {SEARCHES_MAX_NUM} from '../consts/searches';

export class Search {
    store: SearchesStore;
    // original position from server
    serverIdx;
    @observable name;
    @observable isNew;
    @observable ignoreCase;
    @observable newName;
    @observable isNewNameSaved;
    isPrevious: boolean;

    searchIsDirty;
    filters: ObservableMap<string, Filter>;

    constructor(store, data?, filters?) {
        makeObservable(this);
        this.store = store;
        if (data) {
            Object.assign(this, data);
        } else {
            this.name = getLocalizedText(texts.newSearch);
            this.isNew = true;
            const ignoreCaseCash = localStorage.getItem('IgnoreCase');
            if (ignoreCaseCash) {
                this.ignoreCase = JSON.parse(ignoreCaseCash);
            } else {
                const cookies = new Cookies();
                const priorityDlgIgnoreCase = cookies.get('QDlgIgnoreCase');
                this.ignoreCase = priorityDlgIgnoreCase === '1';
            }
        }
        this.filters = observable.map({});
        filters && this.setFilters(filters);

        //mark "filters" as dirty once changed
        reaction(
            () => this.filtersArray.map(value => value),
            () => {
                this.searchIsDirty = true;
            }
        );

        reaction(
            () => this.ignoreCase,
            () => {
                this.searchIsDirty = true;
            }
        );

        this.isNewNameSaved = true;
        this.newName = this.name;
    }

    get filteredColumns() {
        return Array.from(this.filters.keys());
    }

    @action resetIsDirty() {
        this.searchIsDirty = false;
        this.filtersArray.forEach(f => {
            f.isDirty && f.resetIsDirty();
        });
    }

    @computed get isFilterd() {
        //if we modified a filter
        if (this.filtersArray.filter(f => !f.isEmpty).length) return true;
        return false;
    }

    @computed get isDirty() {
        //if we added or remove filters
        if (this.searchIsDirty) return true;
        //if we modified a filter
        if (this.filtersArray.filter(f => f.isDirty).length) return true;
        return false;
    }

    @action setFilters(searchValues) {
        this.filters.clear();
        for (const filter of searchValues) {
            this.setFilter(filter);
        }
    }

    @action setReorderedFilters = reorderedFilters => {
        for (let i = 0; i < reorderedFilters.length; i++) {
            const filter = reorderedFilters[i];
            filter.sortIndex = i;
        }
    };

    @computed get filterValues() {
        return this.filtersArray.slice().sort((a, b) => a.sortIndex - b.sortIndex);
    }

    // set filter from server
    @action setFilter(filter) {
        this.filters.set(filter.column.name, new Filter(this, filter));
    }

    // add new filter
    @action addFilter(column) {
        this.filters.set(
            column.name,
            new Filter(this, {
                column,
                field: column.field,
                operator: getDefaultOperator(column),
                sortIndex: this.filters.size
            })
        );
    }

    @action deleteFilter(key) {
        this.filters.delete(key);
    }

    @action clearFilters() {
        this.filters.clear();
    }

    @action setName(value) {
        this.name = value;
    }

    @action setIgnoreCase(value) {
        this.ignoreCase = value;
    }

    @action setIsNew(value) {
        this.isNew = value;
    }

    @action setIsNewNameSaved(value) {
        this.isNewNameSaved = value;
    }

    @action setNewName(value) {
        this.newName = value;
    }

    @action resetNewName() {
        this.newName = this.name;
    }

    @computed get index(): string {
        if (this.isNew) {
            return this.store.emptyPlace.toString();
        }
        if (this.isPrevious) {
            return this.store.previousList.indexOf(this).toString();
        }
        const indexInSearchesList = this.store.searchesList.indexOf(this).toString();
        return indexInSearchesList !== '-1' ? indexInSearchesList : this.serverIdx.toString();
    }

    @computed get shortKey(): string {
        return parseInt(this.index) <= 9 && 'Ctrl + ' + this.index;
    }

    @computed get isDefault(): boolean {
        return this.store.defaultIdx === this.index;
    }

    @computed get filtersArray(): Array<Filter> {
        return Array.from(this.filters.values());
    }

    @computed get filtersCounter(): number {
        return this.filters && this.filtersArray.filter(f => !f.isEmpty).length;
    }

    @computed get canExecute(): boolean {
        return this.filters && this.filters.size > 0;
    }

    @computed get canSave(): boolean {
        if (this.isPrevious) {
            return this.canExecute && this.store.emptyPlace <= SEARCHES_MAX_NUM;
        } else {
            return this.canExecute && parseInt(this.index) <= SEARCHES_MAX_NUM;
        }
    }

    @action setIsPrevious(value: boolean) {
        this.isPrevious = value;
    }

    @action setServerIdx(index: number) {
        this.serverIdx = index;
    }

    createCopy() {
        return new Search(this.store, this, this.filtersArray);
    }

    updateServerIndex() {
        this.serverIdx = this.index;
    }
}
